import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  DealConfig,
  DealProbability,
  DealReason,
  DealSettings,
  DealStatus,
  PlaceholderProfile,
} from '@forecastify/shared';
import { TextInput, type MenuItem } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Field } from '../card';
import { useDragOrder } from '../settings/settings-shell';
import { SettingsTable, type SettingsRow } from '../settings/settings-table';
import {
  categoryLabel,
  dealMoney,
  dealMoneyShort,
  FORECAST_COLORS,
  forecastLabel,
  seniorityLabel,
} from './deal-kit';
import { amountOf, SwitchRow } from './settings-deal-dialogs';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D1 · the four tabs of Deal menedzsment.           │
 * │ `phase6/16`–`19`.                                                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Pure renderers: the route owns the requests, these own the rows. Every write control is gone
 * for a reader without `settings.manage` — a `blank` cell keeps the grid honest rather than
 * leaving a control that refuses everything it offers.
 */

/** A titled block — the mockup's card heading and its one-paragraph note. */
function Block({
  title,
  note,
  children,
}: {
  readonly title: string;
  readonly note: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-[13px] font-medium">{title}</h2>
        <p className="text-muted-foreground mt-0.5 max-w-[760px] text-[12px] leading-relaxed">
          {note}
        </p>
      </div>
      {children}
    </section>
  );
}

function rowMenu(
  t: Translate,
  name: string,
  items: { onEdit: () => void; onUp?: () => void; onDown?: () => void; onDelete?: () => void },
): MenuItem[] {
  const menu: MenuItem[] = [{ label: t('common.edit'), icon: 'pencil', onSelect: items.onEdit }];
  /* ⚠️ The keyboard path for the grip — `useDragOrder`'s header says why it is not optional. */
  if (items.onUp !== undefined) {
    menu.push({ label: t('settings.moveUp', { name }), icon: 'chevron-up', onSelect: items.onUp });
  }
  if (items.onDown !== undefined) {
    menu.push({
      label: t('settings.moveDown', { name }),
      icon: 'chevron-down',
      onSelect: items.onDown,
    });
  }
  if (items.onDelete !== undefined) {
    menu.push({ type: 'separator' });
    menu.push({
      label: t('common.delete'),
      icon: 'trash',
      destructive: true,
      onSelect: items.onDelete,
    });
  }
  return menu;
}

/* ── Deal státuszok ───────────────────────────────────────────────────────────────────────── */

export function StatusesTab({
  statuses,
  loading,
  canManage,
  onEdit,
  onDelete,
  onReorder,
}: {
  readonly statuses: readonly DealStatus[];
  readonly loading: boolean;
  readonly canManage: boolean;
  readonly onEdit: (status: DealStatus) => void;
  readonly onDelete: (status: DealStatus) => void;
  readonly onReorder: (ids: readonly string[]) => void;
}) {
  const t = useT();
  /* ⚠️ Only the open ones move: the won and lost pair always closes the pipeline, and the server
     refuses an order that names them. */
  const openIds = statuses.filter((status) => status.category === 'open').map((s) => s.id);
  const order = useDragOrder(openIds, canManage ? onReorder : undefined);

  const rows: SettingsRow[] = statuses.map((status) => {
    const open = status.category === 'open';
    const at = openIds.indexOf(status.id);
    return {
      id: status.id,
      ...(open ? order.rowProps(status.id) : {}),
      draggable: open && order.enabled,
      dragging: order.dragging === status.id,
      over: order.over === status.id,
      onOpen: canManage
        ? () => {
            onEdit(status);
          }
        : undefined,
      cells: [
        open && order.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
        { kind: 'name' as const, text: status.name, color: status.color },
        {
          kind: 'badge' as const,
          text: categoryLabel(t, status.category),
          tone: open ? ('outline' as const) : ('secondary' as const),
        },
        { kind: 'num' as const, text: `${String(status.probability)}%` },
        /* A closed deal does not rot, and 0 is the setting for "never". */
        { kind: 'num' as const, text: open && status.rotDays > 0 ? status.rotDays : '–' },
        {
          kind: 'text' as const,
          text: status.criteria,
          className: 'text-muted-foreground truncate',
        },
        { kind: 'num' as const, text: status.dealCount },
        canManage
          ? {
              kind: 'kebab' as const,
              ariaLabel: t('settings.rowMenu', { name: status.name }),
              items: rowMenu(t, status.name, {
                onEdit: () => {
                  onEdit(status);
                },
                ...(open && at > 0
                  ? {
                      onUp: () => {
                        order.moveTo(status.id, at - 1);
                      },
                    }
                  : {}),
                ...(open && at < openIds.length - 1
                  ? {
                      onDown: () => {
                        order.moveTo(status.id, at + 1);
                      },
                    }
                  : {}),
                ...(open
                  ? {
                      onDelete: () => {
                        onDelete(status);
                      },
                    }
                  : {}),
              }),
            }
          : { kind: 'blank' as const },
      ],
    };
  });

  return (
    <Block title={t('dealSettings.statuses.title')} note={t('dealSettings.statuses.note')}>
      <SettingsTable
        grid="34px minmax(160px,1fr) 92px 96px 104px minmax(0,1.6fr) 56px 36px"
        minWidth={880}
        columns={[
          {},
          { label: t('dealSettings.statuses.column.name') },
          { label: t('dealSettings.statuses.column.category') },
          { label: t('dealSettings.statuses.column.probability'), align: 'right' },
          { label: t('dealSettings.statuses.column.rot'), align: 'right' },
          { label: t('dealSettings.statuses.column.criteria') },
          { label: t('dealSettings.statuses.column.deals'), align: 'right' },
          {},
        ]}
        groups={[{ rows }]}
        loading={loading}
        empty={{
          icon: 'kanban',
          title: t('dealSettings.statuses.empty'),
          body: t('dealSettings.statuses.emptyBody'),
        }}
      />
    </Block>
  );
}

/* ── Valószínűségek ───────────────────────────────────────────────────────────────────────── */

export function ProbabilitiesTab({
  probabilities,
  loading,
  canManage,
  onEdit,
  onDelete,
}: {
  readonly probabilities: readonly DealProbability[];
  readonly loading: boolean;
  readonly canManage: boolean;
  readonly onEdit: (probability: DealProbability) => void;
  readonly onDelete: (probability: DealProbability) => void;
}) {
  const t = useT();
  const rows: SettingsRow[] = probabilities.map((step) => ({
    id: step.id,
    onOpen: canManage
      ? () => {
          onEdit(step);
        }
      : undefined,
    cells: [
      { kind: 'dot' as const, text: `${String(step.pct)}%`, color: step.color },
      { kind: 'text' as const, text: step.name, className: 'font-medium' },
      { kind: 'text' as const, text: step.description, className: 'text-muted-foreground' },
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: step.name }),
            items: rowMenu(t, step.name, {
              onEdit: () => {
                onEdit(step);
              },
              onDelete: () => {
                onDelete(step);
              },
            }),
          }
        : { kind: 'blank' as const },
    ],
  }));

  return (
    <Block
      title={t('dealSettings.probabilities.title')}
      note={t('dealSettings.probabilities.note')}
    >
      <SettingsTable
        grid="84px minmax(140px,0.8fr) minmax(0,1.6fr) 36px"
        minWidth={600}
        columns={[
          { label: t('dealSettings.probabilities.column.pct') },
          { label: t('dealSettings.probabilities.column.name') },
          { label: t('dealSettings.probabilities.column.description') },
          {},
        ]}
        groups={[{ rows }]}
        loading={loading}
        empty={{
          icon: 'chart-column',
          title: t('dealSettings.probabilities.empty'),
          body: t('dealSettings.probabilities.emptyBody'),
        }}
      />
    </Block>
  );
}

/* ── Okok ─────────────────────────────────────────────────────────────────────────────────── */

export function ReasonsTab({
  reasons,
  loading,
  canManage,
  onEdit,
  onToggle,
  onDelete,
}: {
  readonly reasons: readonly DealReason[];
  readonly loading: boolean;
  readonly canManage: boolean;
  readonly onEdit: (reason: DealReason) => void;
  readonly onToggle: (reason: DealReason, patch: { active?: boolean; needsNote?: boolean }) => void;
  readonly onDelete: (reason: DealReason) => void;
}) {
  const t = useT();

  const menuFor = (reason: DealReason): SettingsRow['cells'][number] =>
    canManage
      ? {
          kind: 'kebab' as const,
          ariaLabel: t('settings.rowMenu', { name: reason.name }),
          items: rowMenu(t, reason.name, {
            onEdit: () => {
              onEdit(reason);
            },
            /* ⚠️ The mockup: *"Törölni csak olyan okot lehet, amin nincs deal"* — one in use is
               switched off instead, so no Delete is offered for it. */
            ...(reason.dealCount === 0
              ? {
                  onDelete: () => {
                    onDelete(reason);
                  },
                }
              : {}),
          }),
        }
      : { kind: 'blank' as const };

  const countCell = (reason: DealReason) => ({
    kind: 'num' as const,
    text: t('dealSettings.dealCount', { count: reason.dealCount }),
    className: 'text-muted-foreground',
  });

  const activeCell = (reason: DealReason) => ({
    kind: 'switch' as const,
    on: reason.active,
    ariaLabel: t('dealSettings.reasons.activeFor', { name: reason.name }),
    disabled: !canManage,
    onChange: (next: boolean) => {
      onToggle(reason, { active: next });
    },
  });

  /* ⚠️ Grouped by the word the reasons carry, in the order the server sorted them; the ungrouped
     ones close the list. */
  const lostGroups = useMemo(() => {
    const groups = new Map<string, DealReason[]>();
    for (const reason of reasons) {
      if (reason.kind !== 'lost') continue;
      const list = groups.get(reason.groupName) ?? [];
      list.push(reason);
      groups.set(reason.groupName, list);
    }
    return [...groups.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : 0));
  }, [reasons]);

  const won = reasons.filter((reason) => reason.kind === 'won');

  return (
    <div className="flex flex-col gap-6">
      <Block title={t('dealSettings.reasons.lostTitle')} note={t('dealSettings.reasons.lostNote')}>
        <SettingsTable
          grid="minmax(160px,1fr) 80px 150px 80px 36px"
          minWidth={600}
          columns={[
            { label: t('dealSettings.reasons.column.name') },
            { label: t('dealSettings.reasons.column.deals'), align: 'right' },
            { label: t('dealSettings.reasons.column.needsNote') },
            { label: t('dealSettings.reasons.column.active') },
            {},
          ]}
          groups={lostGroups.map(([group, list]) => ({
            label: group === '' ? t('dealSettings.reasons.noGroup') : group,
            rows: list.map((reason) => ({
              id: reason.id,
              onOpen: canManage
                ? () => {
                    onEdit(reason);
                  }
                : undefined,
              cells: [
                {
                  kind: 'text' as const,
                  text: reason.name,
                  className: reason.active ? 'font-medium' : 'text-muted-foreground',
                },
                countCell(reason),
                {
                  kind: 'switch' as const,
                  on: reason.needsNote,
                  ariaLabel: t('dealSettings.reasons.needsNoteFor', { name: reason.name }),
                  disabled: !canManage,
                  onChange: (next: boolean) => {
                    onToggle(reason, { needsNote: next });
                  },
                },
                activeCell(reason),
                menuFor(reason),
              ],
            })),
          }))}
          loading={loading}
          empty={{
            icon: 'flag',
            title: t('dealSettings.reasons.emptyLost'),
            body: t('dealSettings.reasons.emptyBody'),
          }}
        />
      </Block>

      <Block title={t('dealSettings.reasons.wonTitle')} note={t('dealSettings.reasons.wonNote')}>
        <SettingsTable
          grid="minmax(160px,1fr) 80px 80px 36px"
          minWidth={480}
          columns={[
            { label: t('dealSettings.reasons.column.name') },
            { label: t('dealSettings.reasons.column.deals'), align: 'right' },
            { label: t('dealSettings.reasons.column.active') },
            {},
          ]}
          groups={[
            {
              rows: won.map((reason) => ({
                id: reason.id,
                onOpen: canManage
                  ? () => {
                      onEdit(reason);
                    }
                  : undefined,
                cells: [
                  {
                    kind: 'text' as const,
                    text: reason.name,
                    className: reason.active ? 'font-medium' : 'text-muted-foreground',
                  },
                  countCell(reason),
                  activeCell(reason),
                  menuFor(reason),
                ],
              })),
            },
          ]}
          loading={loading}
          empty={{
            icon: 'star',
            title: t('dealSettings.reasons.emptyWon'),
            body: t('dealSettings.reasons.emptyBody'),
          }}
        />
      </Block>
    </div>
  );
}

/* ── Forecast · placeholder ───────────────────────────────────────────────────────────────── */

function forecastDescription(t: Translate, category: DealConfig['forecast'][number]['category']) {
  if (category === 'commit') return t('dealSettings.forecast.desc.commit');
  if (category === 'best_case') return t('dealSettings.forecast.desc.best_case');
  if (category === 'omitted') return t('dealSettings.forecast.desc.omitted');
  return t('dealSettings.forecast.desc.pipeline');
}

export function ForecastTab({
  config,
  loading,
  canManage,
  onSettings,
  onEditProfile,
  onDeleteProfile,
}: {
  readonly config: DealConfig | null;
  readonly loading: boolean;
  readonly canManage: boolean;
  /** The whole settings row with the change applied — the PATCH carries all three. */
  readonly onSettings: (next: {
    nextStepRequired: boolean;
    tentativeBooking: boolean;
    quarterlyTarget: number;
    version: number;
  }) => void;
  readonly onEditProfile: (profile: PlaceholderProfile) => void;
  readonly onDeleteProfile: (profile: PlaceholderProfile) => void;
}) {
  const t = useT();
  const settings = config?.settings ?? null;
  const currency = settings?.currencyCode ?? 'HUF';

  /* The four categories are fixed (`0227`) and read-only; the count and sum are the open deals'. */
  const categoryRows: SettingsRow[] = (config?.forecast ?? []).map((entry) => ({
    id: entry.category,
    cells: [
      {
        kind: 'dot' as const,
        text: forecastLabel(t, entry.category),
        color: FORECAST_COLORS[entry.category],
      },
      {
        kind: 'text' as const,
        text: forecastDescription(t, entry.category),
        className: 'text-muted-foreground',
      },
      { kind: 'num' as const, text: entry.count },
      { kind: 'num' as const, text: dealMoneyShort(entry.value, currency) },
    ],
  }));

  const profileRows: SettingsRow[] = (config?.profiles ?? []).map((profile) => {
    const name = `${profile.projectRoleName} · ${seniorityLabel(t, profile.seniority)}`;
    return {
      id: profile.id,
      onOpen: canManage
        ? () => {
            onEditProfile(profile);
          }
        : undefined,
      cells: [
        { kind: 'name' as const, text: profile.projectRoleName, color: profile.projectRoleColor },
        {
          kind: 'text' as const,
          text: seniorityLabel(t, profile.seniority),
          className: 'text-muted-foreground',
        },
        { kind: 'num' as const, text: dealMoney(profile.costRate, currency) },
        { kind: 'num' as const, text: dealMoney(profile.targetRate, currency) },
        { kind: 'num' as const, text: profile.usage },
        canManage
          ? {
              kind: 'kebab' as const,
              ariaLabel: t('settings.rowMenu', { name }),
              items: rowMenu(t, name, {
                onEdit: () => {
                  onEditProfile(profile);
                },
                ...(profile.usage === 0
                  ? {
                      onDelete: () => {
                        onDeleteProfile(profile);
                      },
                    }
                  : {}),
              }),
            }
          : { kind: 'blank' as const },
      ],
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <Block
        title={t('dealSettings.forecast.categoriesTitle')}
        note={t('dealSettings.forecast.categoriesNote')}
      >
        <SettingsTable
          grid="minmax(140px,0.6fr) minmax(0,1.6fr) 64px 120px"
          minWidth={600}
          columns={[
            { label: t('dealSettings.forecast.column.category') },
            { label: t('dealSettings.forecast.column.meaning') },
            { label: t('dealSettings.forecast.column.count'), align: 'right' },
            { label: t('dealSettings.forecast.column.value'), align: 'right' },
          ]}
          groups={[{ rows: categoryRows }]}
          loading={loading}
          empty={{ icon: 'chart-column', title: '', body: '' }}
        />
      </Block>

      <section className="bg-card text-card-foreground flex flex-col gap-3 rounded-xl border p-4">
        <h2 className="text-[13px] font-medium">{t('dealSettings.forecast.disciplineTitle')}</h2>
        {settings === null ? null : (
          <Discipline settings={settings} canManage={canManage} onSettings={onSettings} />
        )}
      </section>

      <Block title={t('dealSettings.profiles.title')} note={t('dealSettings.profiles.note')}>
        <SettingsTable
          grid="minmax(160px,1fr) 110px 120px 120px 84px 36px"
          minWidth={680}
          columns={[
            { label: t('dealSettings.profiles.column.role') },
            { label: t('dealSettings.profiles.column.seniority') },
            { label: t('dealSettings.profiles.column.cost'), align: 'right' },
            { label: t('dealSettings.profiles.column.target'), align: 'right' },
            { label: t('dealSettings.profiles.column.usage'), align: 'right' },
            {},
          ]}
          groups={[{ rows: profileRows }]}
          loading={loading}
          empty={{
            icon: 'users',
            title: t('dealSettings.profiles.empty'),
            body: t('dealSettings.profiles.emptyBody'),
          }}
        />
      </Block>
    </div>
  );
}

function Discipline({
  settings,
  canManage,
  onSettings,
}: {
  readonly settings: DealSettings;
  readonly canManage: boolean;
  readonly onSettings: (next: {
    nextStepRequired: boolean;
    tentativeBooking: boolean;
    quarterlyTarget: number;
    version: number;
  }) => void;
}) {
  const t = useT();
  const stored = String(Number(settings.quarterlyTarget));
  const [target, setTarget] = useState(stored);
  const [error, setError] = useState<string | null>(null);

  /* A reload (a save, or somebody else's) replaces what is typed only when it changed. */
  useEffect(() => {
    setTarget(stored);
    setError(null);
  }, [stored]);

  const current = {
    nextStepRequired: settings.nextStepRequired,
    tentativeBooking: settings.tentativeBooking,
    quarterlyTarget: Number(settings.quarterlyTarget),
    version: settings.version,
  };

  /* ⚠️ The target commits on blur or Enter, like the settings table's inline input — one PATCH
     per decision rather than one per keystroke. */
  const commitTarget = (): void => {
    if (target === stored) return;
    const amount = amountOf(target);
    if (amount === null) {
      setError(t('dealSettings.forecast.error.target'));
      return;
    }
    onSettings({ ...current, quarterlyTarget: amount });
  };

  return (
    <div className="flex flex-col gap-3">
      <SwitchRow
        label={t('dealSettings.forecast.nextStep')}
        note={t('dealSettings.forecast.nextStepNote')}
        checked={settings.nextStepRequired}
        disabled={!canManage}
        onChange={(next) => {
          onSettings({ ...current, nextStepRequired: next });
        }}
      />
      {/* ⚠️ DM-5: a tentative booking is hidden by default and never counted in utilisation —
          the note says both, because it is the promise the resource screens keep. */}
      <SwitchRow
        label={t('dealSettings.forecast.tentative')}
        note={t('dealSettings.forecast.tentativeNote')}
        checked={settings.tentativeBooking}
        disabled={!canManage}
        onChange={(next) => {
          onSettings({ ...current, tentativeBooking: next });
        }}
      />
      <div className="max-w-[280px]">
        <Field
          label={t('dealSettings.forecast.target', { currency: settings.currencyCode })}
          note={t('dealSettings.forecast.targetNote')}
          {...(error === null ? {} : { error })}
        >
          <TextInput
            inputMode="decimal"
            className="tabular-nums"
            value={target}
            disabled={!canManage}
            onChange={(event) => {
              setTarget(event.target.value);
              setError(null);
            }}
            onBlur={commitTarget}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitTarget();
            }}
          />
        </Field>
      </div>
    </div>
  );
}
